import type { ButtonHTMLAttributes } from "react";
import { cn } from "../../lib/cn.js";

type ButtonVariant = "primary" | "secondary" | "ghost" | "danger";
type ButtonSize = "sm" | "md" | "lg";

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant;
  size?: ButtonSize;
}

const variantClasses: Record<ButtonVariant, string> = {
  primary:
    "border border-accent-strong bg-accent-strong text-on-accent hover:brightness-95",
  secondary:
    "border border-border-default bg-surface-overlay text-content-primary hover:bg-surface-muted",
  ghost:
    "border border-transparent bg-transparent text-content-secondary hover:bg-surface-overlay hover:text-content-primary",
  danger:
    "border border-status-rejected-border bg-status-rejected-bg text-status-rejected-text hover:brightness-95",
};

const sizeClasses: Record<ButtonSize, string> = {
  sm: "px-2.5 py-1.5 text-xs rounded-lg",
  md: "px-4 py-2.5 text-sm rounded-xl",
  lg: "px-5 py-3 text-sm rounded-xl",
};

export function Button({
  className,
  variant = "primary",
  size = "md",
  type = "button",
  ...props
}: ButtonProps) {
  return (
    <button
      type={type}
      className={cn(
        "focus-ring inline-flex min-h-11 items-center justify-center gap-2 font-medium transition-[background-color,border-color,color,box-shadow,filter]",
        "disabled:cursor-not-allowed disabled:opacity-50 motion-reduce:transition-none",
        variantClasses[variant],
        sizeClasses[size],
        className
      )}
      {...props}
    />
  );
}
